import { useState } from 'react';
import { Link } from 'react-router-dom';
import { SEO } from '@/components/SEO';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { ArrowRight, Search, FileText, Download, Globe, Calculator, Eye, RefreshCw } from 'lucide-react';

const helpCategories = [
  {
    icon: <FileText className="h-6 w-6 text-indigo-500" />,
    title: 'Creating Invoices',
    slug: 'creating-invoices',
    articles: [
      'How to create your first free invoice',
      'How to add your business logo to an invoice',
      'How to add multiple line items and services',
      'How to set Net 30, Net 60, or custom payment terms',
      'How to save and reuse invoice templates',
    ],
  },
  {
    icon: <Calculator className="h-6 w-6 text-emerald-500" />,
    title: 'Taxes & Currencies',
    slug: 'taxes-currencies',
    articles: [
      'How to add VAT to an invoice (UK & EU)',
      'How to calculate GST on an Australian invoice',
      'How to apply SST for Malaysian invoices',
      'How to use multiple tax tiers on one invoice',
      'How to switch currencies and set exchange rates',
    ],
  },
  {
    icon: <Download className="h-6 w-6 text-blue-500" />,
    title: 'Downloads & Exports',
    slug: 'downloads-exports',
    articles: [
      'How to download an invoice as PDF',
      'How to export an invoice as Word (.docx)',
      'How to print an invoice directly from browser',
      'Why my PDF looks different from the preview',
      'How to send an invoice by email',
    ],
  },
  {
    icon: <RefreshCw className="h-6 w-6 text-purple-500" />,
    title: 'Quotations & Estimates',
    slug: 'quotations-estimates',
    articles: [
      'How to create a price quotation or estimate',
      'How to convert a quotation into an invoice',
      'Difference between a quotation and an invoice',
      'How to add expiry dates to quotations',
      'How to share a quotation link with a client',
    ],
  },
  {
    icon: <Eye className="h-6 w-6 text-orange-500" />,
    title: 'Client Tracking (Pro)',
    slug: 'client-tracking',
    articles: [
      'How to track when a client opens your invoice',
      'How to view outstanding and overdue invoices',
      'How to use the Pro analytics dashboard',
      'How to set up invoice read receipts',
      'How to manage client payment status',
    ],
  },
  {
    icon: <Globe className="h-6 w-6 text-rose-500" />,
    title: 'Account & Billing',
    slug: 'account-billing',
    articles: [
      'How to create a free account',
      'What is included in the Free plan',
      'How to upgrade to Pro',
      'How to cancel or manage your Pro subscription',
      'How to update your business profile',
    ],
  },
];

const topArticles = [
  { title: 'How to Create a Free Invoice in 3 Steps', href: '/guide#create-invoice' },
  { title: 'How to Add VAT or GST to Your Invoice', href: '/guide#taxes' },
  { title: 'How to Download Your Invoice as PDF or Word', href: '/guide#download' },
  { title: 'How to Convert a Quotation to an Invoice', href: '/guide#quotation-to-invoice' },
  { title: 'Free vs Pro: What Features Do I Get?', href: '/pricing' },
  { title: 'How to Track Client Invoice Opens (Pro Feature)', href: '/guide#tracking' },
];

const structuredData = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  name: 'Quick Free Invoice Tool Help Center',
  description: 'Find answers and step-by-step guides for the Quick Free Invoice Tool — the free online invoice generator for freelancers and small businesses.',
  url: 'https://quickfreeinvoicetool.com/help',
  mainEntity: topArticles.map((a) => ({
    '@type': 'Question',
    name: a.title,
    acceptedAnswer: {
      '@type': 'Answer',
      text: `See our full guide at quickfreeinvoicetool.com${a.href}`,
    },
  })),
};

export default function Help() {
  const [searchQuery, setSearchQuery] = useState('');

  const filteredCategories = helpCategories.map((cat) => ({
    ...cat,
    articles: cat.articles.filter((a) =>
      a.toLowerCase().includes(searchQuery.toLowerCase())
    ),
  })).filter((cat) => searchQuery === '' || cat.articles.length > 0);

  return (
    <div className="min-h-screen bg-white">
      <SEO
        title="Help Center — Quick Free Invoice Tool | Free Invoice Generator Support & How-To Guides"
        description="Get help with Quick Free Invoice Tool. Find step-by-step guides on creating invoices, adding VAT/GST taxes, downloading PDFs, converting quotations to invoices, and more. Free invoice generator support."
        keywords="invoice help, how to create invoice, free invoice generator help, invoice tutorial, vat invoice help, gst invoice guide, pdf invoice download help, quotation to invoice, invoice generator support"
        canonical="https://quickfreeinvoicetool.com/help"
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(structuredData) }}
      />

      {/* Hero */}
      <section className="bg-gradient-to-br from-slate-800 via-slate-900 to-indigo-900 text-white py-20">
        <div className="container mx-auto px-4 text-center max-w-3xl">
          <Badge className="bg-white/20 hover:bg-white/30 text-white border-white/30 mb-4 px-3 py-1 text-xs font-semibold uppercase tracking-wider rounded-full">
            Help Center
          </Badge>
          <h1 className="font-heading text-4xl md:text-5xl font-extrabold mb-6 leading-tight">
            Quick Free Invoice Tool Help Center — How-To Guides & Invoice Support
          </h1>
          <p className="text-slate-300 text-lg mb-8">
            Search our help articles or browse by category below. Find step-by-step answers for creating invoices, adding taxes, downloading PDFs, and more.
          </p>
          <div className="relative max-w-xl mx-auto">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
            <Input
              placeholder="Search help articles... e.g. 'add VAT' or 'download PDF'"
              className="pl-12 h-14 text-slate-900 bg-white text-base rounded-xl border-0 shadow-lg"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
        </div>
      </section>

      {/* Top Articles */}
      {searchQuery === '' && (
        <section className="py-14 bg-slate-50 border-b">
          <div className="container mx-auto px-4 max-w-4xl">
            <h2 className="font-heading text-2xl font-bold mb-6 text-slate-900">
              Most Popular Help Articles
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {topArticles.map((article) => (
                <Link
                  key={article.title}
                  to={article.href}
                  className="flex items-center gap-3 bg-white border border-slate-200 rounded-lg px-4 py-3 hover:border-indigo-300 hover:shadow-sm transition-all group"
                >
                  <ArrowRight className="h-4 w-4 text-indigo-500 shrink-0 group-hover:translate-x-1 transition-transform" />
                  <span className="text-sm font-medium text-slate-700 group-hover:text-indigo-600 transition-colors">{article.title}</span>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Help Categories */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-slate-900">
              Browse Help Topics for Quick Free Invoice Tool
            </h2>
            <p className="text-slate-500 mt-3 text-lg max-w-2xl mx-auto">
              Find step-by-step articles for every feature of our free invoice generator.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto">
            {filteredCategories.map((cat) => (
              <Card key={cat.title} className="border-slate-200/80 hover:shadow-md transition-shadow h-full">
                <CardHeader>
                  <div className="w-10 h-10 rounded-lg bg-slate-100 flex items-center justify-center mb-3">
                    {cat.icon}
                  </div>
                  <CardTitle className="font-heading text-lg text-slate-900">{cat.title}</CardTitle>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-2">
                    {cat.articles.map((article) => (
                      <li key={article}>
                        <Link
                          to="/guide"
                          className="text-sm text-slate-600 hover:text-indigo-600 transition-colors flex items-start gap-2"
                        >
                          <ArrowRight className="h-3.5 w-3.5 text-indigo-400 mt-0.5 shrink-0" />
                          {article}
                        </Link>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Quick Actions */}
      <section className="py-20 bg-slate-50 border-t">
        <div className="container mx-auto px-4 max-w-4xl text-center">
          <h2 className="font-heading text-3xl font-bold mb-4 text-slate-900">
            Still Need Help? Contact Quick Free Invoice Tool Support
          </h2>
          <p className="text-slate-500 mb-8 text-lg">
            Our team is ready to help you with any questions about invoicing, billing, or your account.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a href="mailto:support@quickfreeinvoicetool.com">
              <Button size="lg" className="bg-indigo-600 hover:bg-indigo-700 text-white font-bold">
                Email Support
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </a>
            <Link to="/faq">
              <Button size="lg" variant="outline" className="border-indigo-300 text-indigo-600 hover:bg-indigo-50">
                View FAQ
              </Button>
            </Link>
          </div>
          <p className="mt-6 text-sm text-slate-400">
            support@quickfreeinvoicetool.com — We respond within 24 hours on business days.
          </p>
        </div>
      </section>
    </div>
  );
}
